import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  User, Shield, ChevronRight,
  Camera, ArrowLeft, FileText,
  Edit3, Save, LogOut, AlertTriangle, X, Lock,
  MapPin, Calendar, Wallet, BookOpen,
  Clock, Plus, Trash2, Building2, CheckCircle,
  Phone, MessageCircle, Search, Send,
  Award, GraduationCap, Globe, ToggleLeft, ToggleRight, LocateFixed, ShieldAlert, ShieldCheck,
  Stethoscope, Heart, Home, Activity,
} from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import { useAuth } from '@contexts/AuthContext'
import { useProviderAuth } from '@hooks/useProviderAuth'
import { providerDashboardService } from '@lib/supabase/providerDashboard.service'
import { supabase } from '@lib/supabase/client'
import { locationEvidenceService, type WebLocationPermissionState } from '@lib/supabase/locationEvidence.service'
import { queryKeys } from '@lib/queryKeys'
import { analyticsService, getAmountBucket } from '@lib/analytics/posthog.service'
import { PRESET_SERVICE_AREAS } from '@lib/constants/nigerianServiceAreas'
import Button from '@components/web/Button'
import ConfirmationModal from '@components/web/ConfirmationModal'

type ActiveView =
  | 'main'
  | 'personal'
  | 'service-areas'
  | 'service-rates'
  | 'availability'
  | 'payout'
  | 'security'
  | 'terms'
  | 'induction'
  | 'support'

const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say']
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

function NairaIcon({ size = 18, className = '' }: { size?: number; className?: string }) {
  const sizeClass = size >= 32 ? 'text-[32px]' : size >= 20 ? 'text-[20px]' : 'text-[18px]'

  return (
    <span
      className={clsx('inline-flex items-center justify-center font-bold leading-none', sizeClass, className)}
      aria-hidden="true"
    >
      ₦
    </span>
  )
}

function getInitials(name?: string | null) {
  if (!name) return '?'
  return name.trim().split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()
}

function PanelHeader({ title, onBack, action }: { title: string; onBack: () => void; action?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <button type="button" title="Go back" aria-label="Go back" onClick={onBack} className="p-1.5 rounded-lg hover:bg-muted">
        <ArrowLeft size={18} />
      </button>
      <h2 className="font-semibold text-foreground flex-1">{title}</h2>
      {action}
    </div>
  )
}

function MenuRow({
  icon: Icon,
  title,
  subtitle,
  onClick,
  disabled,
  showChevron = true,
  iconBg = 'bg-muted',
  iconColor = 'text-muted-foreground',
}: {
  icon: any
  title: string
  subtitle?: string
  onClick: () => void
  disabled?: boolean
  showChevron?: boolean
  iconBg?: string
  iconColor?: string
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-muted transition-colors border-t border-border first:border-t-0 disabled:opacity-50 disabled:pointer-events-none text-left"
    >
      <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center shrink-0', iconBg)}>
        <Icon size={17} className={iconColor} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</p>}
      </div>
      {showChevron && <ChevronRight size={16} className="text-muted-foreground shrink-0" />}
    </button>
  )
}

function ErrorAlert({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2.5 bg-destructive/10 border border-destructive/20 rounded-xl text-sm text-destructive">
      <AlertTriangle size={14} /> {message}
    </div>
  )
}
function SuccessAlert({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
      <CheckCircle size={14} /> {message}
    </div>
  )
}

function PersonalDetailsPanel({
  providerData,
  onBack,
  onSaved,
}: {
  providerData: {
    name: string
    phone: string
    email: string
    specialty: string
    experience: string
    gender: string
    date_of_birth: string
    address: string
    qualification: string
    license_number: string
    languages: string[]
    specializations: string[]
    bio: string
  }
  onBack: () => void
  onSaved: () => void
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving]   = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [form, setForm] = useState({
    name:            providerData.name,
    phone_number:    providerData.phone,
    specialty:       providerData.specialty,
    experience:      providerData.experience,
    gender:          providerData.gender,
    date_of_birth:   providerData.date_of_birth,
    address:         providerData.address,
    qualification:   providerData.qualification,
    languages:       providerData.languages.join(', '),
    specializations: providerData.specializations.join(', '),
    bio:             providerData.bio,
  })

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError(null)
    const result = await providerDashboardService.updateProfile({
      name:            form.name,
      phone_number:    form.phone_number,
      specialty:       form.specialty,
      experience:      form.experience,
      gender:          form.gender,
      date_of_birth:   form.date_of_birth || null,
      address:         form.address,
      qualification:   form.qualification,
      languages:       form.languages.split(',').map((s) => s.trim()).filter(Boolean),
      specializations: form.specializations.split(',').map((s) => s.trim()).filter(Boolean),
      bio:             form.bio,
    })
    setIsSaving(false)
    if (result.success) {
      setIsEditing(false)
      onSaved()
    } else {
      setSaveError(result.error ?? 'Failed to save changes.')
    }
  }

  const Field = ({ label, field, type = 'text', placeholder }: { label: string; field: keyof typeof form; type?: string; placeholder?: string }) => (
    <div className="space-y-1.5">
      <label htmlFor={`provider-profile-${String(field)}`} className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</label>
      <input
        id={`provider-profile-${String(field)}`}
        type={type}
        title={label}
        aria-label={label}
        value={form[field]}
        onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
        disabled={!isEditing}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:bg-muted placeholder:text-muted-foreground/50"
      />
    </div>
  )

  return (
    <div className="space-y-5">
      <PanelHeader
        title="Profile Details"
        onBack={onBack}
        action={
          isEditing ? (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => { setIsEditing(false); setSaveError(null) }}>
                <X size={13} className="mr-1" /> Cancel
              </Button>
              <Button size="sm" isLoading={isSaving} onClick={handleSave}>
                <Save size={13} className="mr-1.5" /> Save
              </Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Edit3 size={13} className="mr-1.5" /> Edit
            </Button>
          )
        }
      />
      {/* Basic Information */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Basic Information</p>
        <div className="bg-card border border-border p-5 space-y-4">
          <Field label="Full Name"     field="name" />
          <Field label="Phone Number"  field="phone_number" />
          <div className="space-y-1.5">
            <label htmlFor="provider-profile-email" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Email Address</label>
            <input
              id="provider-profile-email"
              type="email"
              title="Email Address"
              aria-label="Email Address"
              value={providerData.email}
              disabled
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-muted text-sm text-muted-foreground opacity-70 cursor-not-allowed"
            />
            <p className="text-xs text-muted-foreground">Email is managed through your account settings and cannot be edited here.</p>
          </div>
          <Field label="Date of Birth" field="date_of_birth" type="date" />
          <div className="space-y-1.5">
            <label htmlFor="provider-profile-gender" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Gender</label>
            <select
              id="provider-profile-gender"
              title="Gender"
              aria-label="Gender"
              value={form.gender}
              onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}
              disabled={!isEditing}
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:bg-muted"
            >
              <option value="">Select gender</option>
              {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Location */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Location</p>
        <div className="bg-card border border-border p-5">
          <div className="space-y-1.5">
            <label htmlFor="provider-profile-address" className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <MapPin size={12} /> Service Location
            </label>
            <input
              id="provider-profile-address"
              type="text"
              title="Service Location"
              aria-label="Service Location"
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              disabled={!isEditing}
              placeholder="e.g. Garki, Lekki, Rumuola"
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:bg-muted placeholder:text-muted-foreground/50"
            />
          </div>
        </div>
      </div>

      {/* Professional Information */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Professional Information</p>
        <div className="bg-card border border-border p-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Award size={12} /> Qualification
            </label>
            <input
              type="text"
              value={form.qualification}
              onChange={(e) => setForm((f) => ({ ...f, qualification: e.target.value }))}
              disabled={!isEditing}
              placeholder="e.g. MBBS, RN, BSc Nursing"
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:bg-muted placeholder:text-muted-foreground/50"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <GraduationCap size={12} /> Medical Specialty
            </label>
            <input
              type="text"
              value={form.specialty}
              onChange={(e) => setForm((f) => ({ ...f, specialty: e.target.value }))}
              disabled={!isEditing}
              placeholder="e.g. General Practice, Paediatrics"
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:bg-muted placeholder:text-muted-foreground/50"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="provider-profile-license-number" className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <FileText size={12} /> License Number
            </label>
            <input
              id="provider-profile-license-number"
              type="text"
              title="License Number"
              aria-label="License Number"
              value={providerData.license_number}
              disabled
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-muted text-sm text-muted-foreground opacity-70 cursor-not-allowed"
            />
            <p className="text-xs text-muted-foreground">License information is verified by our team and cannot be edited here.</p>
          </div>
          <Field label="Years of Experience" field="experience" placeholder="e.g. 5" />
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Globe size={12} /> Languages Spoken
            </label>
            <input
              type="text"
              value={form.languages}
              onChange={(e) => setForm((f) => ({ ...f, languages: e.target.value }))}
              disabled={!isEditing}
              placeholder="e.g. English, Yoruba, Igbo"
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:bg-muted placeholder:text-muted-foreground/50"
            />
            {isEditing && <p className="text-xs text-muted-foreground">Separate multiple languages with commas.</p>}
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Award size={12} /> Specializations
            </label>
            <input
              type="text"
              value={form.specializations}
              onChange={(e) => setForm((f) => ({ ...f, specializations: e.target.value }))}
              disabled={!isEditing}
              placeholder="e.g. Wound Care, Post-surgery Care"
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:bg-muted placeholder:text-muted-foreground/50"
            />
            {isEditing && <p className="text-xs text-muted-foreground">Separate multiple specializations with commas.</p>}
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Professional Bio</label>
            <textarea
              rows={4}
              value={form.bio}
              onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
              disabled={!isEditing}
              placeholder="Tell patients about your experience and approach to healthcare..."
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:bg-muted resize-none placeholder:text-muted-foreground/50"
            />
          </div>
        </div>
      </div>

      {saveError && <ErrorAlert message={saveError} />}
    </div>
  )
}

type ServiceArea = {
  id: string
  state: string
  country: string
  is_active: boolean
  travel_radius: number | null
}

function ServiceAreasPanel({ providerId, onBack }: { providerId: string; onBack: () => void }) {
  const queryClient = useQueryClient()
  const [saving, setSaving]       = useState<string | null>(null)
  const [error, setError]         = useState<string | null>(null)
  const [radiusEdit, setRadiusEdit] = useState<Record<string, string>>({})

  const serviceAreasQuery = useQuery({
    queryKey: queryKeys.provider.serviceAreas(providerId),
    queryFn: async () => {
      let areas = await providerDashboardService.getServiceAreas(providerId) as ServiceArea[]

      const existingKeys = new Set(areas.map((area) => `${area.country}:${area.state}`))
      const missingDefaults = PRESET_SERVICE_AREAS.filter(
        (area) => !existingKeys.has(`${area.country}:${area.state}`)
      )

      if (missingDefaults.length > 0) {
        await Promise.all(
          missingDefaults.map((area, index) =>
            providerDashboardService.createServiceArea(providerId, {
              country: area.country,
              state: area.state,
              travel_radius: 10,
              is_active: areas.length === 0 && index === 0,
            })
          )
        )
        areas = await providerDashboardService.getServiceAreas(providerId) as ServiceArea[]
      }

      return areas.sort((a, b) => {
        const aIndex = PRESET_SERVICE_AREAS.findIndex((preset) => preset.state === a.state)
        const bIndex = PRESET_SERVICE_AREAS.findIndex((preset) => preset.state === b.state)
        return aIndex - bIndex
      })
    },
    enabled: !!providerId,
  })

  const areas = serviceAreasQuery.data ?? []
  const isLoading = serviceAreasQuery.isPending

  const refreshServiceAreas = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.provider.serviceAreas(providerId) })
    await serviceAreasQuery.refetch()
  }

  const handleToggle = async (area: ServiceArea) => {
    if (!area.is_active) {
      const others = areas.filter((a) => a.id !== area.id && a.is_active)
      for (const other of others) {
        await providerDashboardService.updateServiceArea(other.id, { is_active: false })
      }
    }
    setSaving(area.id)
    setError(null)
    const result = await providerDashboardService.updateServiceArea(area.id, { is_active: !area.is_active })
    setSaving(null)
    if (result.success) { await refreshServiceAreas() } else { setError(result.error ?? 'Failed to update') }
  }

  const handleRadiusSave = async (area: ServiceArea) => {
    const radius = parseInt(radiusEdit[area.id] ?? '')
    if (isNaN(radius) || radius < 1) { setError('Enter a valid radius (km)'); return }
    setSaving(area.id + '-r')
    setError(null)
    const result = await providerDashboardService.updateServiceArea(area.id, { travel_radius: radius })
    setSaving(null)
    if (result.success) {
      setRadiusEdit((r) => { const n = { ...r }; delete n[area.id]; return n })
      await refreshServiceAreas()
    } else { setError(result.error ?? 'Failed to update') }
  }

  return (
    <div className="space-y-5">
      <PanelHeader title="Service Area" onBack={onBack} />
      <p className="text-sm text-muted-foreground -mt-3">Enable the area you serve. Only one area can be active at a time.</p>
      {isLoading ? (
        <div className="space-y-3">{[1,2,3].map((i) => <div key={i} className="h-20 bg-muted border animate-pulse" />)}</div>
      ) : areas.length === 0 ? (
        <div className="bg-card border border-border p-6 text-center text-sm text-muted-foreground">
          No service areas found. Contact support if this is unexpected.
        </div>
      ) : (
        <div className="space-y-3">
          {areas.map((area) => {
            const isEditingRadius = area.id in radiusEdit
            return (
              <div key={area.id} className={clsx('bg-card border border p-4 space-y-3 transition-all', area.is_active ? 'border-primary/40' : 'border-border')}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center', area.is_active ? 'bg-primary/10' : 'bg-muted')}>
                      <MapPin size={16} className={area.is_active ? 'text-primary' : 'text-muted-foreground'} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{area.state}</p>
                      <p className="text-xs text-muted-foreground">{area.country}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    title={area.is_active ? `Disable ${area.state}` : `Enable ${area.state}`}
                    aria-label={area.is_active ? `Disable ${area.state}` : `Enable ${area.state}`}
                    onClick={() => handleToggle(area)}
                    disabled={saving === area.id}
                    className={clsx('relative w-11 h-6 rounded-full transition-colors', area.is_active ? 'bg-primary' : 'bg-muted-foreground/30', saving === area.id && 'opacity-60')}
                  >
                    <span className={clsx('absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform', area.is_active && 'translate-x-5')} />
                  </button>
                </div>
                <div className="flex items-center gap-2 pt-1 border-t border-border">
                  <span className="text-xs text-muted-foreground flex-1">Travel radius</span>
                  {isEditingRadius ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        min={1}
                        title={`Travel radius for ${area.state}`}
                        aria-label={`Travel radius for ${area.state}`}
                        value={radiusEdit[area.id]}
                        onChange={(e) => setRadiusEdit((r) => ({ ...r, [area.id]: e.target.value }))}
                        className="w-20 px-2 py-1 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <span className="text-xs text-muted-foreground">km</span>
                      <button onClick={() => handleRadiusSave(area)} disabled={saving === area.id + '-r'} className="px-2.5 py-1 text-xs bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-60">
                        {saving === area.id + '-r' ? '...' : 'Save'}
                      </button>
                      <button type="button" title={`Cancel radius edit for ${area.state}`} aria-label={`Cancel radius edit for ${area.state}`} onClick={() => setRadiusEdit((r) => { const n = { ...r }; delete n[area.id]; return n })} className="p-1 rounded-lg hover:bg-muted">
                        <X size={13} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-foreground">{area.travel_radius ?? '—'} km</span>
                      <button type="button" title={`Edit radius for ${area.state}`} aria-label={`Edit radius for ${area.state}`} onClick={() => setRadiusEdit((r) => ({ ...r, [area.id]: String(area.travel_radius ?? '') }))} className="p-1 rounded-lg hover:bg-muted">
                        <Edit3 size={12} className="text-muted-foreground" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
      {error && <ErrorAlert message={error} />}
    </div>
  )
}

interface TimeSlot { id: string; startTime: string; endTime: string }
interface DayAvailability { day: string; isAvailable: boolean; availabilityId?: string; timeSlots: TimeSlot[] }

function AvailabilityPanel({ providerId, onBack }: { providerId: string; onBack: () => void }) {
  const queryClient = useQueryClient()
  const [saving, setSaving]       = useState<string | null>(null)
  const [error, setError]         = useState<string | null>(null)

  const availabilityQuery = useQuery({
    queryKey: queryKeys.provider.availability(providerId),
    queryFn: async () => {
      const db = await providerDashboardService.getAvailability(providerId)
      return DAYS_OF_WEEK.map((day) => {
        const row = db.find((item: any) => item.day_of_week === day)
        return {
          day,
          isAvailable: row?.is_available ?? false,
          availabilityId: row?.id,
          timeSlots: (row?.provider_time_slots ?? []).map((slot: any) => ({
            id: slot.id,
            startTime: slot.start_time ?? '09:00',
            endTime: slot.end_time ?? '17:00',
          })),
        }
      }) as DayAvailability[]
    },
    enabled: !!providerId,
  })

  const schedule = availabilityQuery.data ?? []
  const isLoading = availabilityQuery.isPending

  const refreshAvailability = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.provider.availability(providerId) })
    await availabilityQuery.refetch()
  }

  const toggleDay = async (day: string, isAvailable: boolean) => {
    setSaving(day)
    setError(null)
    const result = await providerDashboardService.upsertDayAvailability(providerId, day, isAvailable)
    if (result.success) {
      await refreshAvailability()
    } else { setError(result.error ?? 'Failed to update') }
    setSaving(null)
  }

  const addSlot = async (day: string) => {
    const dayData = schedule.find((d) => d.day === day)
    if (!dayData) return
    let availId = dayData.availabilityId
    if (!availId) {
      const r = await providerDashboardService.upsertDayAvailability(providerId, day, true)
      if (!r.success || !r.id) { setError(r.error ?? 'Failed'); return }
      availId = r.id
    }
    setSaving(day + '-add')
    const result = await providerDashboardService.addTimeSlot(availId, '09:00', '17:00')
    setSaving(null)
    if (result.success) { await refreshAvailability() } else { setError(result.error ?? 'Failed') }
  }

  const removeSlot = async (slotId: string) => {
    setSaving(slotId)
    const result = await providerDashboardService.deleteTimeSlot(slotId)
    setSaving(null)
    if (result.success) { await refreshAvailability() } else { setError(result.error ?? 'Failed') }
  }

  return (
    <div className="space-y-5">
      <PanelHeader title="Availability" onBack={onBack} />
      {isLoading ? (
        <div className="space-y-3">{DAYS_OF_WEEK.map((d) => <div key={d} className="h-16 bg-muted border animate-pulse" />)}</div>
      ) : (
        <div className="space-y-3">
          {schedule.map((dayData) => (
            <div key={dayData.day} className={clsx('bg-card border border p-4 space-y-3 transition-all', dayData.isAvailable ? 'border-primary/40' : 'border-border')}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">{dayData.day}</p>
                <button
                  type="button"
                  title={dayData.isAvailable ? `Disable ${dayData.day}` : `Enable ${dayData.day}`}
                  aria-label={dayData.isAvailable ? `Disable ${dayData.day}` : `Enable ${dayData.day}`}
                  onClick={() => toggleDay(dayData.day, !dayData.isAvailable)}
                  disabled={saving === dayData.day}
                  className={clsx('relative w-11 h-6 rounded-full transition-colors', dayData.isAvailable ? 'bg-primary' : 'bg-muted-foreground/30', saving === dayData.day && 'opacity-60')}
                >
                  <span className={clsx('absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform', dayData.isAvailable && 'translate-x-5')} />
                </button>
              </div>
              {dayData.isAvailable && (
                <div className="space-y-2 pt-1 border-t border-border">
                  {dayData.timeSlots.map((slot) => (
                    <div key={slot.id} className="flex items-center gap-2">
                      <input type="time" title={`${dayData.day} start time`} aria-label={`${dayData.day} start time`} defaultValue={slot.startTime} className="flex-1 px-2 py-1.5 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary" />
                      <span className="text-xs text-muted-foreground">to</span>
                      <input type="time" title={`${dayData.day} end time`} aria-label={`${dayData.day} end time`} defaultValue={slot.endTime} className="flex-1 px-2 py-1.5 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary" />
                      <button type="button" title={`Remove time slot on ${dayData.day}`} aria-label={`Remove time slot on ${dayData.day}`} onClick={() => removeSlot(slot.id)} disabled={saving === slot.id} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive disabled:opacity-50">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => addSlot(dayData.day)} disabled={saving === dayData.day + '-add'} className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 py-1 disabled:opacity-50">
                    <Plus size={13} /> Add Time Slot
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {error && <ErrorAlert message={error} />}
    </div>
  )
}

type PayoutMethod = {
  id: string
  method_type: string
  account_name: string
  account_number: string
  bank_name: string
  bank_code?: string | null
  is_default?: boolean | null
}
type WalletData = { balance: number; currency?: string | null }
type BankFormState = {
  bank_name: string
  account_name: string
  account_number: string
}

const PROVIDER_BANK_OPTIONS = [
  'Access Bank',
  'Citibank',
  'Ecobank',
  'Fidelity Bank',
  'First Bank',
  'First City Monument Bank (FCMB)',
  'Globus Bank',
  'Guaranty Trust Bank (GTBank)',
  'Heritage Bank',
  'Jaiz Bank',
  'Keystone Bank',
  'Kuda Bank',
  'Nova Bank Limited',
  'Optimus Bank Limited',
  'Parallex Bank',
  'Polaris Bank',
  'Providus Bank',
  'Alternative Bank',
  'Jaiz Bank PLC',
  'Lotus Bank Limited',
  'Taj Bank Limited',
  'Premium Trust Bank',
  'Stanbic IBTC Bank',
  'Standard Chartered Bank',
  'Sterling Bank',
  'Suntrust Bank',
  'Titan Trust Bank',
  'Union Bank',
  'United Bank for Africa (UBA)',
  'Unity Bank',
  'Wema Bank',
  'Zenith Bank',
] as const

type WalletTransaction = {
  id: string
  type: 'earning' | 'withdrawal'
  amount: number
  platformFee: number
  description: string
  status?: string
  createdAt: string
}

function BankAccountModal({
  initialValues,
  isLoading,
  title,
  onClose,
  onSubmit,
}: {
  initialValues: BankFormState
  isLoading: boolean
  title: string
  onClose: () => void
  onSubmit: (values: BankFormState) => void
}) {
  const [form, setForm] = useState<BankFormState>(initialValues)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setForm(initialValues)
    setError(null)
  }, [initialValues])

  const handleSubmit = () => {
    if (!form.bank_name.trim() || !form.account_name.trim() || !form.account_number.trim()) {
      setError('Please fill in all bank account fields.')
      return
    }

    setError(null)
    onSubmit({
      bank_name: form.bank_name.trim(),
      account_name: form.account_name.trim(),
      account_number: form.account_number.trim(),
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="w-full max-w-lg bg-card border border-border p-5 shadow-xl">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">Please ensure your bank details are accurate. Withdrawals will be processed to this account.</p>

        <div className="mt-4 space-y-4">
          <div>
            <label htmlFor="provider-bank-name" className="block text-sm font-medium text-foreground mb-2">Bank Name</label>
            <select
              id="provider-bank-name"
              value={form.bank_name}
              onChange={(event) => setForm((current) => ({ ...current, bank_name: event.target.value }))}
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select a bank...</option>
              {PROVIDER_BANK_OPTIONS.map((bankName) => (
                <option key={bankName} value={bankName}>{bankName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Account Number</label>
            <input
              type="text"
              inputMode="numeric"
              value={form.account_number}
              onChange={(event) => setForm((current) => ({ ...current, account_number: event.target.value.replace(/[^0-9]/g, '') }))}
              placeholder="Enter account number"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Account Name</label>
            <input
              type="text"
              value={form.account_name}
              onChange={(event) => setForm((current) => ({ ...current, account_name: event.target.value }))}
              placeholder="Enter account name"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>Cancel</Button>
          <Button onClick={handleSubmit} isLoading={isLoading}>Save</Button>
        </div>
      </div>
    </div>
  )
}

function WithdrawFundsModal({
  isLoading,
  maxAmount,
  onClose,
  onSubmit,
}: {
  isLoading: boolean
  maxAmount: number
  onClose: () => void
  onSubmit: (amount: number) => void
}) {
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = () => {
    const normalizedAmount = Number(amount.replace(/,/g, '').trim())

    if (!amount.trim() || Number.isNaN(normalizedAmount)) {
      setError('Please enter a valid amount.')
      return
    }

    if (normalizedAmount <= 0) {
      setError('Withdrawal amount must be greater than zero.')
      return
    }

    if (normalizedAmount > maxAmount) {
      setError('Withdrawal amount cannot exceed your available balance.')
      return
    }

    setError(null)
    onSubmit(normalizedAmount)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="w-full max-w-md bg-card border border-border p-5 shadow-xl">
        <h3 className="text-lg font-semibold text-foreground">Withdraw Funds</h3>
        <p className="mt-1 text-sm text-muted-foreground">Available: {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(maxAmount)}</p>

        <div className="mt-4 space-y-2">
          <label htmlFor="withdraw-amount" className="block text-sm font-medium text-foreground">Amount</label>
          <input
            id="withdraw-amount"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="Enter amount"
            className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {error && (
          <div className="mt-3 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>Cancel</Button>
          <Button onClick={handleSubmit} isLoading={isLoading}>Withdraw</Button>
        </div>
      </div>
    </div>
  )
}

type ServiceRateItem = {
  id: string
  service_category_id: string
  service_name: string
  description?: string | null
  duration?: string | null
  min_rate?: number | null
  max_rate?: number | null
  base_rate?: number | null
  is_active: boolean
  has_rate: boolean
}

const SERVICE_ICONS: Record<string, React.ElementType> = {
  'Doctor Visit':      Stethoscope,
  'Nurse Care':        User,
  'Home Care':         Home,
  'Specialized Care':  Heart,
  Dermatology:         Activity,
  'Chat Consultation': MessageCircle,
}
const SERVICE_COLORS: Record<string, string> = {
  'Doctor Visit':      'bg-blue-50 text-blue-600',
  'Specialized Care':        'bg-red-50 text-red-600',
  'Nurse Care':         'bg-emerald-50 text-emerald-600',
  'Home Care':  'bg-purple-50 text-purple-600',
  Dermatology: 'bg-teal-50 text-teal-700',
  'Chat Consultation': 'bg-sky-50 text-sky-600',
}
const DERMATOLOGY_ICON = '/dermatology.png'

function getCanonicalServiceName(serviceName: string) {
  const normalized = serviceName.trim().toLowerCase()

  if (normalized === 'doctor visit' || normalized.includes('doctor')) return 'Doctor Visit'
  if (normalized === 'nurse care' || normalized.includes('nurse')) return 'Nurse Care'
  if (normalized === 'home care' || normalized.includes('home')) return 'Home Care'
  if (normalized === 'specialized care' || normalized.includes('special')) return 'Specialized Care'
  if (normalized === 'dermatology' || normalized.includes('dermat')) return 'Dermatology'
  if (normalized === 'chat consultation' || normalized.includes('chat')) return 'Chat Consultation'

  return serviceName
}

function ServiceRatesPanel({ providerId, onBack }: { providerId: string; onBack: () => void }) {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [rateInput, setRateInput] = useState('')
  const [saving, setSaving]       = useState(false)
  const [error, setError]         = useState<string | null>(null)

  const serviceRatesQuery = useQuery({
    queryKey: queryKeys.provider.serviceRates(providerId),
    queryFn: () => providerDashboardService.getServiceRates(providerId),
    enabled: !!providerId,
  })

  const rates = serviceRatesQuery.data ?? []
  const isLoading = serviceRatesQuery.isPending

  const refreshServiceRates = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.provider.serviceRates(providerId) })
    await serviceRatesQuery.refetch()
  }

  const openEdit = (item: ServiceRateItem) => {
    setEditingId(item.id)
    setRateInput(item.base_rate ? String(item.base_rate) : '')
    setError(null)
  }

  const handleSave = async (item: ServiceRateItem) => {
    const num = parseInt(rateInput.replace(/,/g, ''), 10)
    const min = item.min_rate ?? 0
    const max = item.max_rate ?? 999_999_999
    if (!num || num < min || num > max) {
      setError(`Rate must be between ${fmtN(min)} and ${fmtN(max)}`)
      return
    }
    setSaving(true)
    setError(null)
    const result = item.has_rate
      ? await providerDashboardService.updateServiceRate(item.id, { base_rate: num })
      : await providerDashboardService.upsertServiceRate(providerId, item.service_category_id, num)
    setSaving(false)
    if (result.success) {
      setEditingId(null)
      await refreshServiceRates()
    } else {
      setError(result.error ?? 'Failed to save rate.')
    }
  }

  const handleToggle = async (item: ServiceRateItem) => {
    if (!item.has_rate) return
    const result = await providerDashboardService.updateServiceRate(item.id, { is_active: !item.is_active })
    if (result.success) {
      await refreshServiceRates()
    } else {
      setError(result.error ?? 'Failed to update service state.')
    }
  }

  const fmtN = (n: number) => n.toLocaleString('en-NG')
  const fmtCurrency = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(n)

  const activeCount = rates.filter((r) => r.is_active).length

  return (
    <div className="space-y-5">
      <PanelHeader title="Service Rates" onBack={onBack} />

      {/* Stats bar */}
      {!isLoading && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-card border border-border p-4 text-center">
            <p className="text-2xl font-bold text-foreground">{activeCount}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Active Services</p>
          </div>
          <div className="bg-card border border-border p-4 text-center">
            <p className="text-2xl font-bold text-primary">{rates.length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Total Services</p>
          </div>
        </div>
      )}

      {/* Rates list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-card border border-border p-5 space-y-3 animate-pulse">
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-muted shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-3 bg-muted rounded w-2/3" />
                </div>
              </div>
              <div className="h-3 bg-muted rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : rates.length === 0 ? (
        <div className="bg-card border border-border p-8 text-center">
          <NairaIcon size={32} className="text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No service categories found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rates.map((item) => {
            const canonicalServiceName = getCanonicalServiceName(item.service_name)
            const IconComp = SERVICE_ICONS[canonicalServiceName] ?? NairaIcon
            const colorClass = SERVICE_COLORS[canonicalServiceName] ?? 'bg-muted text-muted-foreground'
            const isEditing = editingId === item.id
            return (
              <div key={item.id} className="bg-card border border-border p-5 space-y-4">
                {/* Header row */}
                <div className="flex items-start gap-3">
                  <div className={clsx('w-10 h-10 rounded-full flex items-center justify-center shrink-0', colorClass)}>
                    {canonicalServiceName === 'Dermatology' ? (
                      <img
                        src={DERMATOLOGY_ICON}
                        alt=""
                        aria-hidden="true"
                        className="h-5 w-5"
                      />
                    ) : (
                      <IconComp size={18} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">{item.service_name}</p>
                    {item.description && <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{item.description}</p>}
                    {item.duration && <p className="text-xs text-muted-foreground mt-0.5">Duration: {item.duration}</p>}
                  </div>
                  {/* Active toggle — only shown once rate is set */}
                  {item.has_rate && (
                    <button
                      onClick={() => handleToggle(item)}
                      className={clsx('shrink-0 transition-colors', item.is_active ? 'text-emerald-600' : 'text-muted-foreground')}
                      title={item.is_active ? 'Disable service' : 'Enable service'}
                    >
                      {item.is_active
                        ? <ToggleRight size={28} />
                        : <ToggleLeft  size={28} />}
                    </button>
                  )}
                </div>

                {/* Rate display */}
                <div className="flex items-center justify-between rounded-xl bg-muted px-4 py-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Your Rate</p>
                    <p className="text-base font-bold text-foreground mt-0.5">
                      {item.base_rate ? fmtCurrency(item.base_rate) : '—'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Market Range</p>
                    <p className="text-xs text-foreground font-medium mt-0.5">
                      {item.min_rate && item.max_rate
                        ? `₦${fmtN(item.min_rate)} – ₦${fmtN(item.max_rate)}`
                        : '—'}
                    </p>
                  </div>
                </div>

                {/* Edit form */}
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Enter your rate (₦)</label>
                      <div className="flex items-center border border-border rounded-xl overflow-hidden bg-background">
                        <span className="px-3 text-sm font-medium text-muted-foreground border-r border-border">₦</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={rateInput}
                          onChange={(e) => setRateInput(e.target.value.replace(/[^0-9]/g, ''))}
                          placeholder="e.g. 15000"
                          className="flex-1 px-3 py-2.5 text-sm bg-background focus:outline-none"
                          autoFocus
                        />
                      </div>
                      {item.min_rate && item.max_rate && (
                        <p className="text-xs text-muted-foreground">Must be between ₦{fmtN(item.min_rate)} and ₦{fmtN(item.max_rate)}</p>
                      )}
                    </div>
                    {error && <p className="text-xs text-destructive">{error}</p>}
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => { setEditingId(null); setError(null) }}>Cancel</Button>
                      <Button size="sm" className="flex-1" isLoading={saving} onClick={() => handleSave(item)}>Save Rate</Button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => openEdit(item)}
                    className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium"
                  >
                    <Edit3 size={12} /> {item.has_rate ? 'Edit Rate' : 'Set Rate'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 border p-4 text-xs text-amber-800 space-y-1 leading-relaxed">
        <p className="font-semibold">Rate Guidelines</p>
        <p>• Rates must fall within the platform's market range</p>
        <p>• Competitive rates attract more patients</p>
        <p>• Toggle a service off to stop accepting that type of booking</p>
        <p>• You can adjust rates at any time</p>
      </div>
    </div>
  )
}

function PayoutWalletPanel({ providerId, onBack }: { providerId: string; onBack: () => void }) {
  const queryClient = useQueryClient()
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [isWithdrawing, setIsWithdrawing] = useState(false)
  const [withdrawStatus, setWithdrawStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [showBankModal, setShowBankModal] = useState(false)
  const [editingMethod, setEditingMethod] = useState<PayoutMethod | null>(null)
  const [isSavingBank, setIsSavingBank] = useState(false)
  const [pendingDeleteMethod, setPendingDeleteMethod] = useState<PayoutMethod | null>(null)
  const [bankStatus, setBankStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const payoutQuery = useQuery({
    queryKey: queryKeys.provider.payout(providerId),
    queryFn: async () => {
      const [wallet, methods, summary, transactions, availableBalance] = await Promise.all([
        providerDashboardService.getWallet(providerId),
        providerDashboardService.getPayoutMethods(providerId),
        providerDashboardService.getEarningsSummary(providerId),
        providerDashboardService.getTransactionLogs(providerId, 10),
        providerDashboardService.getAvailableBalance(providerId),
      ])

      return {
        wallet: wallet as WalletData | null,
        methods: methods as PayoutMethod[],
        summary,
        transactions: transactions as WalletTransaction[],
        availableBalance,
      }
    },
    enabled: !!providerId,
  })

  const wallet = payoutQuery.data?.wallet ?? null
  const methods = payoutQuery.data?.methods ?? []
  const summary = payoutQuery.data?.summary ?? null
  const transactions = payoutQuery.data?.transactions ?? []
  const availableBalance = payoutQuery.data?.availableBalance ?? 0
  const isLoading = payoutQuery.isPending
  const hasBalance = availableBalance > 0
  const primaryMethod = methods.find((method) => method.is_default) ?? methods[0] ?? null

  const fmtCurrency = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(n)

  const fmtTransactionDate = (value: string) =>
    new Date(value).toLocaleString('en-NG', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  const refreshPayoutData = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.provider.payout(providerId) })
    await payoutQuery.refetch()
  }

  const openAddBankModal = () => {
    setEditingMethod(null)
    setBankStatus(null)
    setShowBankModal(true)
  }

  const openEditBankModal = (method: PayoutMethod) => {
    setEditingMethod(method)
    setBankStatus(null)
    setShowBankModal(true)
  }

  const handleSaveBankAccount = async (values: BankFormState) => {
    setIsSavingBank(true)
    setBankStatus(null)

    const result = editingMethod
      ? await providerDashboardService.updatePayoutMethod(editingMethod.id, providerId, values)
      : await providerDashboardService.addPayoutMethod(providerId, {
          method_type: 'bank_account',
          account_name: values.account_name,
          account_number: values.account_number,
          bank_name: values.bank_name,
        })

    setIsSavingBank(false)

    if (result.success) {
      setShowBankModal(false)
      setEditingMethod(null)
      setBankStatus({ type: 'success', message: editingMethod ? 'Bank account updated successfully.' : 'Bank account added successfully.' })
      await refreshPayoutData()
      return
    }

    setBankStatus({ type: 'error', message: result.error ?? 'Failed to save bank account.' })
  }

  const handleSetPrimaryMethod = async (methodId: string) => {
    setBankStatus(null)
    const result = await providerDashboardService.setDefaultPayoutMethod(methodId, providerId)

    if (result.success) {
      setBankStatus({ type: 'success', message: 'Primary account updated.' })
      await refreshPayoutData()
      return
    }

    setBankStatus({ type: 'error', message: result.error ?? 'Failed to set primary account.' })
  }

  const handleDeleteBankAccount = async () => {
    if (!pendingDeleteMethod) return

    setIsSavingBank(true)
    const result = await providerDashboardService.deletePayoutMethod(pendingDeleteMethod.id)
    setIsSavingBank(false)

    if (result.success) {
      setPendingDeleteMethod(null)
      setBankStatus({ type: 'success', message: 'Bank account deleted successfully.' })
      await refreshPayoutData()
      return
    }

    setBankStatus({ type: 'error', message: result.error ?? 'Failed to delete bank account.' })
  }

  const handleWithdrawInitiation = () => {
    setWithdrawStatus(null)

    if (!hasBalance) {
      setWithdrawStatus({ type: 'error', message: 'Your available balance is currently zero.' })
      return
    }

    if (!methods.length) {
      setWithdrawStatus({ type: 'error', message: 'Please add a bank account in the mobile app before withdrawing.' })
      return
    }

    setShowWithdrawModal(true)
    analyticsService.capture('withdrawal_started', {
      source: 'provider_payout_screen',
      amount_bucket: getAmountBucket(availableBalance || wallet?.balance || 0),
      platform: 'web',
    })
  }

  const handleProcessWithdrawal = async (amount: number) => {
    if (!primaryMethod) {
      setWithdrawStatus({ type: 'error', message: 'No bank account is available for this withdrawal.' })
      return
    }

    setIsWithdrawing(true)
    setWithdrawStatus(null)

    const result = await providerDashboardService.requestWithdrawal(providerId, amount, primaryMethod.id)

    setIsWithdrawing(false)

    if (result.success) {
      analyticsService.capture('withdrawal_requested', {
        source: 'provider_payout_screen',
        amount_bucket: getAmountBucket(amount),
        platform: 'web',
      })
      setShowWithdrawModal(false)
      setWithdrawStatus({ type: 'success', message: 'Withdrawal request submitted. You will receive a notification once it is processed.' })
      await refreshPayoutData()
      return
    }

    setWithdrawStatus({ type: 'error', message: result.error ?? 'Failed to process withdrawal.' })
    analyticsService.capture('withdrawal_failed', {
      source: 'provider_payout_screen',
      error_type: 'service_returned_failure',
      amount_bucket: getAmountBucket(amount),
      platform: 'web',
    })
  }

  return (
    <div className="space-y-5">
      <PanelHeader title="Payout & Wallet" onBack={onBack} />

      {/* Wallet balance card */}
      <div className="bg-primary border p-5 text-white">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Wallet size={16} className="opacity-80" />
              <span className="text-sm opacity-80">Available Balance</span>
            </div>
            {isLoading ? <div className="h-8 w-32 bg-white/20 border animate-pulse" /> : <p className="text-3xl font-bold">{fmtCurrency(availableBalance || wallet?.balance || 0)}</p>}
          </div>
          <Button
            variant="outline"
            className="border-white/40 bg-white/10 text-white hover:bg-white/20"
            onClick={handleWithdrawInitiation}
            disabled={isLoading || isWithdrawing || !hasBalance}
            isLoading={isWithdrawing}
          >
            Withdraw
          </Button>
        </div>
      </div>

      {withdrawStatus && (
        <div className={clsx(
          'rounded-xl border px-4 py-3 text-sm',
          withdrawStatus.type === 'success'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : 'border-destructive/20 bg-destructive/10 text-destructive'
        )}>
          {withdrawStatus.message}
        </div>
      )}

      {bankStatus && (
        <div className={clsx(
          'rounded-xl border px-4 py-3 text-sm',
          bankStatus.type === 'success'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : 'border-destructive/20 bg-destructive/10 text-destructive'
        )}>
          {bankStatus.message}
        </div>
      )}

      {/* Earnings Summary — all time */}
      <div className="space-y-2">
        <p className="text-base font-bold text-foreground">Earnings Summary</p>
        {isLoading ? (
          <div className="bg-card border border-border p-4 space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-5 bg-muted rounded animate-pulse" />)}
          </div>
        ) : (
          <div className="bg-card border border-border overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-border">
              <p className="text-sm text-muted-foreground">Total Earned</p>
              <p className="text-sm font-semibold text-foreground">{fmtCurrency(summary?.totalEarned ?? 0)}</p>
            </div>
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-border">
              <p className="text-sm text-muted-foreground">Platform Fee (20%)</p>
              <p className="text-sm font-semibold text-destructive">-{fmtCurrency(summary?.platformFee ?? 0)}</p>
            </div>
            <div className="flex items-center justify-between px-4 py-3.5">
              <p className="text-sm font-bold text-foreground">Net Credited</p>
              <p className="text-sm font-bold text-emerald-600">{fmtCurrency(summary?.netCredited ?? 0)}</p>
            </div>
          </div>
        )}
      </div>

      {/* Registered bank accounts — read-only */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foreground">Registered Bank Accounts</h3>
          <Button size="sm" onClick={openAddBankModal}>
            <Plus size={14} className="mr-1.5" /> Add
          </Button>
        </div>
        {isLoading ? (
          <div className="h-16 bg-muted rounded-2xl animate-pulse" />
        ) : methods.length === 0 ? (
          <div className="bg-card border border-border p-5 text-center text-sm text-muted-foreground space-y-3">
            <p>No bank accounts registered yet.</p>
            <div>
              <Button size="sm" onClick={openAddBankModal}>
                <Plus size={14} className="mr-1.5" /> Add Bank Account
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {methods.map((m) => (
              <div key={m.id} className="bg-card border border-border p-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center shrink-0">
                    <Building2 size={16} className="text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{m.account_name}</p>
                    <p className="text-xs text-muted-foreground">{m.bank_name} · ••••{m.account_number.slice(-4)}</p>
                  </div>
                  {m.is_default && (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Primary</span>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {!m.is_default && (
                    <Button size="sm" variant="outline" onClick={() => handleSetPrimaryMethod(m.id)}>
                      <CheckCircle size={14} className="mr-1.5" /> Set Primary
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEditBankModal(m)}>
                    <Edit3 size={14} className="mr-1.5" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/5" onClick={() => setPendingDeleteMethod(m)}>
                    <Trash2 size={14} className="mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foreground">Recent Transactions</h3>
          {transactions.length > 5 && !isLoading && (
            <span className="text-xs text-muted-foreground">Showing latest 10</span>
          )}
        </div>

        {isLoading ? (
          <div className="bg-card border border-border p-4 space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-14 bg-muted rounded animate-pulse" />)}
          </div>
        ) : transactions.length === 0 ? (
          <div className="bg-card border border-border p-5 text-center text-sm text-muted-foreground">
            No transactions yet.
          </div>
        ) : (
          <div className="bg-card border border-border overflow-hidden">
            <div className="max-h-96 overflow-y-auto divide-y divide-border">
              {transactions.map((transaction) => (
                <div key={transaction.id} className="flex items-start justify-between gap-4 px-4 py-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground leading-5">{transaction.description}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{fmtTransactionDate(transaction.createdAt)}</p>
                    {transaction.platformFee > 0 && (
                      <p className="mt-1 text-xs text-muted-foreground">Fee: {fmtCurrency(transaction.platformFee)}</p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className={clsx(
                      'text-sm font-semibold',
                      transaction.type === 'earning' ? 'text-emerald-600' : 'text-destructive'
                    )}>
                      {transaction.type === 'earning' ? '+' : '-'}{fmtCurrency(transaction.amount)}
                    </p>
                    {transaction.status && (
                      <span className={clsx(
                        'mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium',
                        transaction.status === 'Paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                      )}>
                        {transaction.status}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showWithdrawModal && (
        <WithdrawFundsModal
          isLoading={isWithdrawing}
          maxAmount={availableBalance}
          onClose={() => setShowWithdrawModal(false)}
          onSubmit={handleProcessWithdrawal}
        />
      )}

      {showBankModal && (
        <BankAccountModal
          initialValues={editingMethod ? {
            bank_name: editingMethod.bank_name ?? '',
            account_name: editingMethod.account_name,
            account_number: editingMethod.account_number,
          } : {
            bank_name: '',
            account_name: '',
            account_number: '',
          }}
          isLoading={isSavingBank}
          title={editingMethod ? 'Edit Bank Account' : 'Add Bank Account'}
          onClose={() => {
            setShowBankModal(false)
            setEditingMethod(null)
          }}
          onSubmit={handleSaveBankAccount}
        />
      )}

      {pendingDeleteMethod && (
        <ConfirmationModal
          title="Delete bank account?"
          message={`Are you sure you want to delete the account ending in ${pendingDeleteMethod.account_number.slice(-4)}?`}
          confirmLabel="Delete"
          confirmVariant="destructive"
          isLoading={isSavingBank}
          onCancel={() => setPendingDeleteMethod(null)}
          onConfirm={handleDeleteBankAccount}
        />
      )}
    </div>
  )
}

function PrivacySecurityPanel({ onBack, providerId }: { onBack: () => void; providerId?: string | null }) {
  const [form, setForm]         = useState({ newPassword: '', confirmPassword: '' })
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError]       = useState<string | null>(null)
  const [success, setSuccess]   = useState(false)
  const [locationPermission, setLocationPermission] = useState<WebLocationPermissionState>('unknown')
  const [isLocationTrackingEnabled, setIsLocationTrackingEnabled] = useState(false)
  const [isRequestingLocationAccess, setIsRequestingLocationAccess] = useState(false)

  const loadLocationStatus = useCallback(async () => {
    const permissionState = await locationEvidenceService.getPermissionState()
    setLocationPermission(permissionState)

    if (!providerId) {
      setIsLocationTrackingEnabled(false)
      return
    }

    const trackingEnabled = await locationEvidenceService.isLocationTrackingEnabled('provider', providerId)
    setIsLocationTrackingEnabled(trackingEnabled)
  }, [providerId])

  useEffect(() => {
    void loadLocationStatus()
  }, [loadLocationStatus])

  useEffect(() => {
    const handleWindowFocus = () => {
      void loadLocationStatus()
    }

    window.addEventListener('focus', handleWindowFocus)
    return () => window.removeEventListener('focus', handleWindowFocus)
  }, [loadLocationStatus])

  const handleRequestLocationAccess = useCallback(async () => {
    setIsRequestingLocationAccess(true)
    const nextState = await locationEvidenceService.requestForegroundPermission()
    setLocationPermission(nextState)
    if (providerId) {
      const trackingEnabled = await locationEvidenceService.isLocationTrackingEnabled('provider', providerId)
      setIsLocationTrackingEnabled(trackingEnabled)
    }
    setIsRequestingLocationAccess(false)
  }, [providerId])

  const locationStatusConfig = (() => {
    if (!isLocationTrackingEnabled) {
      return {
        icon: ShieldAlert,
        iconClassName: 'text-slate-600',
        badgeClassName: 'bg-slate-100 text-slate-700 border-slate-200',
        badgeLabel: 'Location off',
        title: 'Location is disabled for this provider account.',
        description: 'Enable Location in the HomiCareplus mobile app to allow provider evidence capture during scheduled appointment windows.',
      }
    }

    if (locationPermission === 'granted') {
      return {
        icon: ShieldCheck,
        iconClassName: 'text-emerald-600',
        badgeClassName: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        badgeLabel: 'Active',
        title: 'Browser location access is enabled.',
        description: 'Provider location can be captured on this device during active appointment windows.',
      }
    }

    if (locationPermission === 'denied') {
      return {
        icon: ShieldAlert,
        iconClassName: 'text-red-600',
        badgeClassName: 'bg-red-50 text-red-700 border-red-200',
        badgeLabel: 'Blocked',
        title: 'Browser location access is blocked.',
        description: 'Allow location permission in your browser settings to resume provider evidence capture on this device.',
        actionLabel: 'Retry Access',
        action: handleRequestLocationAccess,
      }
    }

    if (locationPermission === 'unsupported') {
      return {
        icon: ShieldAlert,
        iconClassName: 'text-amber-600',
        badgeClassName: 'bg-amber-50 text-amber-700 border-amber-200',
        badgeLabel: 'Unsupported',
        title: 'This browser cannot capture geolocation evidence.',
        description: 'Use a supported browser on HTTPS or localhost if you need provider location evidence logging on web.',
      }
    }

    return {
      icon: LocateFixed,
      iconClassName: 'text-primary',
      badgeClassName: 'bg-primary/10 text-primary border-primary/20',
      badgeLabel: 'Action needed',
      title: 'Location access still needs approval on this browser.',
      description: 'Grant browser location access so provider evidence tracking can run during scheduled appointment windows.',
      actionLabel: 'Enable Location Access',
      action: handleRequestLocationAccess,
    }
  })()

  const LocationStatusIcon = locationStatusConfig.icon

  const handleSave = async () => {
    setError(null)
    if (form.newPassword.length < 12) { setError('Password must be at least 12 characters'); return }
    if (!/[A-Z]/.test(form.newPassword) || !/[0-9]/.test(form.newPassword) || !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(form.newPassword)) {
      setError('Password must include an uppercase letter, a number, and a special character'); return
    }
    if (form.newPassword !== form.confirmPassword) { setError('Passwords do not match'); return }
    setIsSaving(true)
    const { error: err } = await supabase.auth.updateUser({ password: form.newPassword })
    setIsSaving(false)
    if (err) { setError(err.message) } else { setSuccess(true); setForm({ newPassword: '', confirmPassword: '' }) }
  }

  return (
    <div className="space-y-5">
      <PanelHeader title="Privacy & Security" onBack={onBack} />
      <div className="bg-card border border-border p-5 space-y-4">
        <div className="flex items-center gap-2.5 pb-2 border-b border-border">
          <Lock size={16} className="text-primary" />
          <p className="text-sm font-semibold text-foreground">Change Password</p>
        </div>
        {success && <SuccessAlert message="Password updated successfully." />}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">New Password</label>
          <input type="password" value={form.newPassword} onChange={(e) => { setForm((f) => ({ ...f, newPassword: e.target.value })); setError(null); setSuccess(false) }} placeholder="Min 12 characters" autoComplete="new-password" className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Confirm New Password</label>
          <input type="password" value={form.confirmPassword} onChange={(e) => { setForm((f) => ({ ...f, confirmPassword: e.target.value })); setError(null); setSuccess(false) }} placeholder="Re-enter new password" autoComplete="new-password" className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
        </div>
        {error && <ErrorAlert message={error} />}
        <Button className="w-full" isLoading={isSaving} onClick={handleSave}>Update Password</Button>
      </div>
      <div className="bg-card border border-border p-5">
        <div className="flex items-center gap-2.5 pb-2 border-b border-border">
          <LocateFixed size={16} className="text-primary" />
          <p className="text-sm font-semibold text-foreground">Location</p>
        </div>

        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
              <LocationStatusIcon size={20} className={locationStatusConfig.iconClassName} />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-foreground">Browser permission status</p>
                <span className={clsx('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold', locationStatusConfig.badgeClassName)}>
                  {locationStatusConfig.badgeLabel}
                </span>
              </div>
              <p className="mt-1 text-sm text-foreground">{locationStatusConfig.title}</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">{locationStatusConfig.description}</p>
            </div>
          </div>

          {locationStatusConfig.action && locationStatusConfig.actionLabel ? (
            <button
              type="button"
              title={locationStatusConfig.actionLabel}
              onClick={locationStatusConfig.action}
              disabled={isRequestingLocationAccess}
              className="inline-flex items-center justify-center gap-2 border border-border bg-background px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isRequestingLocationAccess ? 'Requesting...' : locationStatusConfig.actionLabel}
            </button>
          ) : null}
        </div>
      </div>
      <div className="bg-muted/50 border border-border border p-4 flex items-start gap-3">
        <Shield size={16} className="text-muted-foreground shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-foreground">Two-Factor Authentication</p>
          <p className="text-xs text-muted-foreground mt-0.5">Biometric authentication and 2FA settings are managed through the HomiCareplus mobile app.</p>
        </div>
      </div>
    </div>
  )
}

const POLICY_DOCS = [
  { title: 'Provider Terms of Service', description: 'Terms and conditions for healthcare providers', color: '#2563EB', fetchKey: 'terms' as const },
  { title: 'Privacy Policy',            description: 'How we handle your professional information',  color: '#059669', fetchKey: 'privacy' as const },
  { title: 'HomiCareplus Code of Conduct', description: 'Professional standards and safety expectations', color: '#7C3AED', fetchKey: 'conduct' as const },
]

const POLICY_SUMMARIES = [
  {
    title: 'Provider Eligibility & Compliance',
    content: 'Providers must maintain valid professional licenses and comply with applicable medical regulations and platform verification requirements.',
  },
  {
    title: 'Service Standards',
    content: 'Providers are expected to deliver professional care, maintain punctuality, follow safety protocols, and document visits appropriately in the platform records.',
  },
  {
    title: 'Payments & Platform Fees',
    content: 'Earnings are processed through the platform according to the agreed fee structure, and all transactions must follow approved in-app payment workflows.',
  },
  {
    title: 'Data Privacy & Confidentiality',
    content: 'Patient and provider information must be handled confidentially with strict adherence to NDPA requirements and internal access controls.',
  },
  {
    title: 'Account Conduct & Enforcement',
    content: 'Policy breaches including non-circumvention, misconduct, or repeated safety violations may result in suspension or permanent account deactivation.',
  },
]

type PolicyDocContent = { title: string; content: string; updatedAt?: string } | null

export function ProviderTermsPage() {
  const { provider } = useProviderAuth()
  const navigate = useNavigate()
  const [openSection, setOpenSection] = useState<string | null>(null)
  const [selectedDocKey, setSelectedDocKey] = useState<'terms' | 'privacy' | 'conduct'>('terms')

  const policyDocumentsQuery = useQuery({
    queryKey: queryKeys.provider.policyDocuments(provider?.id),
    queryFn: async () => {
      const [terms, privacy, conduct] = await Promise.all([
        providerDashboardService.getProviderTerms(),
        providerDashboardService.getProviderPrivacyPolicy(),
        providerDashboardService.getProviderCodeOfConduct(),
      ])

      return { terms, privacy, conduct }
    },
    enabled: !!provider?.id,
  })

  const docsLoading = policyDocumentsQuery.isPending
  const docContents: Record<string, PolicyDocContent> = policyDocumentsQuery.data ?? {}

  const fmtDate = (d?: string) => {
    if (!d) return 'June 15, 2026'
    try { return new Date(d).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' }) } catch { return 'June 15, 2026' }
  }

  const activeDoc = POLICY_DOCS.find((doc) => doc.fetchKey === selectedDocKey) ?? POLICY_DOCS[0]
  const activeContent = docContents[selectedDocKey] ?? { title: activeDoc.title, content: '' }

  return (
    <div className="p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button type="button" title="Back to profile" aria-label="Back to profile" onClick={() => navigate('/provider/profile')} className="p-1.5 rounded-lg hover:bg-muted">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-base font-bold text-foreground">Terms & Conditions</h1>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border p-4 sm:p-5">
          <div className="flex flex-wrap gap-2">
            {POLICY_DOCS.map((doc) => (
              <button
                key={doc.title}
                type="button"
                onClick={() => setSelectedDocKey(doc.fetchKey)}
                className={clsx(
                  'px-3 py-2 rounded-xl border text-sm font-medium transition-colors',
                  selectedDocKey === doc.fetchKey
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-muted'
                )}
              >
                {doc.title}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border overflow-hidden">
          <div className="border-b border-border px-5 py-4">
            <p className="text-base font-semibold text-foreground">{activeContent.title}</p>
            <p className="mt-1 text-xs text-muted-foreground">Last updated: {fmtDate(activeContent.updatedAt)}</p>
          </div>

          <div className="px-5 py-5">
            {activeContent.content
              ? activeContent.content.split('\n').map((line, i) => (
                  <p key={i} className={clsx('text-sm text-foreground/80 leading-relaxed', line.trim() === '' ? 'mb-3' : 'mb-1')}>{line}</p>
                ))
              : (
                <p className="text-sm text-muted-foreground">{docsLoading ? 'Loading document...' : 'Content not available.'}</p>
              )}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-lg font-semibold text-foreground">Policy Summary</p>
          {POLICY_SUMMARIES.map((section) => {
            const isOpen = openSection === section.title
            return (
              <div key={section.title} className="bg-card border border-border overflow-hidden">
                <button onClick={() => setOpenSection(isOpen ? null : section.title)} className="w-full flex items-center justify-between px-4 py-3.5 text-left">
                  <span className="text-sm font-semibold text-foreground pr-3">{section.title}</span>
                  <span className={clsx('text-xl font-light text-muted-foreground shrink-0 transition-transform duration-200 leading-none', isOpen && 'rotate-45')}>+</span>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 border-t border-border">
                    <p className="text-sm text-muted-foreground leading-relaxed mt-3">{section.content}</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function TermsPanel({ onBack }: { onBack: () => void }) {
  return (
    <div className="space-y-5">
      <PanelHeader title="Terms & Conditions" onBack={onBack} />
      <ProviderTermsPage />
    </div>
  )
}

// ── Provider Support Panel ──────────────────────────────────────────────────
function SupportPanel({ providerId, onBack }: { providerId: string; onBack: () => void }) {
  const [search, setSearch]     = useState('')
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null)
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedback, setFeedback] = useState({ subject: '', message: '', rating: 0 })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fbSuccess, setFbSuccess] = useState(false)
  const [fbError, setFbError]   = useState<string | null>(null)

  const supportQuery = useQuery({
    queryKey: queryKeys.provider.support(providerId),
    queryFn: async () => {
      const [config, faqs] = await Promise.all([
        providerDashboardService.getAppConfig(),
        providerDashboardService.getFAQs('provider'),
      ])

      return { config, faqs }
    },
    enabled: !!providerId,
  })

  const config = supportQuery.data?.config ?? null
  const faqs = supportQuery.data?.faqs ?? []
  const isLoading = supportQuery.isPending

  const handleSubmitFeedback = async () => {
    if (!feedback.message.trim()) { setFbError('Please enter a message'); return }
    setIsSubmitting(true); setFbError(null)
    const result = await providerDashboardService.submitSupportFeedback({
      providerId,
      subject: feedback.subject,
      message: feedback.message,
      rating:  feedback.rating > 0 ? feedback.rating : undefined,
    })
    setIsSubmitting(false)
    if (result.success) {
      setFbSuccess(true)
      setFeedback({ subject: '', message: '', rating: 0 })
    } else { setFbError(result.error ?? 'Failed to submit') }
  }

  const filteredFAQs = faqs.filter((f) =>
    f.title.toLowerCase().includes(search.toLowerCase()) ||
    f.content.toLowerCase().includes(search.toLowerCase())
  )

  const phone    = config?.supportPhone    ?? '+2349132371175'
  const whatsapp = config?.supportWhatsApp ?? '+2349132371175'

  return (
    <div className="space-y-5">
      <PanelHeader title="Provider Support" onBack={onBack} />

      {isLoading ? (
        <div className="space-y-3">
          {[1,2,3].map((i) => <div key={i} className="h-20 bg-muted border animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* Contact options */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Get Help</p>
            <a href={`tel:${phone}`} className="flex items-center gap-3 bg-card border border-border p-4 hover:bg-muted/50 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <Phone size={18} className="text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">Phone Support</p>
                <p className="text-xs text-muted-foreground">Mon–Fri, 8 AM – 5 PM · Immediate</p>
              </div>
            </a>
            <a href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-card border border-border p-4 hover:bg-muted/50 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <MessageCircle size={18} className="text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">WhatsApp Chat</p>
                <p className="text-xs text-muted-foreground">Available 24 hrs · Under 30 mins</p>
              </div>
            </a>
            <button onClick={() => { setShowFeedback((v) => !v); setFbSuccess(false); setFbError(null) }} className="w-full flex items-center gap-3 bg-card border border-border p-4 hover:bg-muted/50 transition-colors text-left">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <Send size={18} className="text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">Send Feedback</p>
                <p className="text-xs text-muted-foreground">Share feedback &amp; help us improve</p>
              </div>
            </button>
          </div>

          {/* Feedback form */}
          {showFeedback && (
            <div className="bg-card border border-border p-4 space-y-3">
              {fbSuccess ? (
                <SuccessAlert message="Feedback submitted — thank you!" />
              ) : (
                <>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Subject (optional)</label>
                    <input type="text" value={feedback.subject} onChange={(e) => setFeedback((f) => ({ ...f, subject: e.target.value }))} placeholder="e.g. App issue" className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Message <span className="text-destructive">*</span></label>
                    <textarea rows={3} value={feedback.message} onChange={(e) => setFeedback((f) => ({ ...f, message: e.target.value }))} placeholder="Tell us what you think..." className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Rating (optional)</label>
                    <div className="flex gap-1">
                      {[1,2,3,4,5].map((star) => (
                        <button key={star} onClick={() => setFeedback((f) => ({ ...f, rating: f.rating === star ? 0 : star }))} className={clsx('text-xl transition-colors', feedback.rating >= star ? 'text-yellow-400' : 'text-muted-foreground/40')}>&#9733;</button>
                      ))}
                    </div>
                  </div>
                  {fbError && <ErrorAlert message={fbError} />}
                  <Button className="w-full" isLoading={isSubmitting} onClick={handleSubmitFeedback}>Submit Feedback</Button>
                </>
              )}
            </div>
          )}

          {/* FAQ section */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Frequently Asked Questions</p>
            <div className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-xl">
              <Search size={14} className="text-muted-foreground shrink-0" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search FAQs..." className="flex-1 text-sm bg-transparent focus:outline-none placeholder:text-muted-foreground" />
            </div>
            {filteredFAQs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">{search ? 'No FAQs match your search.' : 'No FAQs available yet.'}</p>
            ) : (
              <div className="space-y-2">
                {filteredFAQs.map((faq) => (
                  <div key={faq.id} className="bg-card border border-border overflow-hidden">
                    <button onClick={() => setExpandedFAQ((id) => id === faq.id ? null : faq.id)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/50 transition-colors">
                      <p className="text-sm font-medium text-foreground pr-2">{faq.title}</p>
                      <span className="text-muted-foreground text-lg leading-none shrink-0">{expandedFAQ === faq.id ? '−' : '+'}</span>
                    </button>
                    {expandedFAQ === faq.id && (
                      <div className="px-4 pb-4 border-t border-border">
                        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{faq.content}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function InductionPanel({ onBack }: { onBack: () => void }) {
  return (
    <div className="space-y-5">
      <PanelHeader title="Provider Induction Training" onBack={onBack} />
      <div className="bg-card border border-border p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
            <BookOpen size={22} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">Welcome to HomiCareplus</p>
            <p className="text-xs text-muted-foreground">Provider onboarding program</p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          The Provider Induction Training covers platform guidelines, patient interaction standards, appointment management, and compliance requirements.
        </p>
        <div className="space-y-2.5 pt-2 border-t border-border">
          {[
            'Platform orientation and app navigation',
            'Patient safety and interaction guidelines',
            'Appointment and documentation standards',
            'Compliance and regulatory requirements',
          ].map((text) => (
            <div key={text} className="flex items-start gap-2.5">
              <CheckCircle size={15} className="text-emerald-600 shrink-0 mt-0.5" />
              <p className="text-sm text-foreground">{text}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <Calendar size={16} className="text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-700">The full interactive training module is available on the <strong>HomiCareplus mobile app</strong>. Download the app to complete your induction.</p>
      </div>
    </div>
  )
}

export default function ProviderProfile() {
  const navigate = useNavigate()
  const location  = useLocation()
  const { logout } = useAuth()
  const { provider, isLoading: providerLoading, refreshProvider } = useProviderAuth()

  const initialView = location.pathname === '/provider/support'
    ? 'support'
    : (location.state as { view?: ActiveView } | null)?.view ?? 'main'
  const [activeView, setActiveView]             = useState<ActiveView>(initialView)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [isSigningOut, setIsSigningOut]         = useState(false)
  const [showSignOutModal, setShowSignOutModal] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [reloadKey, setReloadKey] = useState(0)
  const triggerReload = useCallback(() => setReloadKey((k) => k + 1), [])
  useEffect(() => { if (reloadKey > 0) refreshProvider?.() }, [reloadKey, refreshProvider])

  const handleImageFile = async (file: File) => {
    setIsUploadingImage(true)
    const result = await providerDashboardService.uploadProfileImage(file)
    setIsUploadingImage(false)
    if (result.success) { triggerReload() } else { alert(result.error ?? 'Failed to upload photo.') }
  }

  const handleSignOut = async () => {
    setIsSigningOut(true)
    try {
      await logout()
    } finally {
      navigate('/auth/login?role=provider', { replace: true })
    }
  }

  const initials  = getInitials(provider?.name)
  const verStatus = provider?.account_status
  const isVerified = provider?.is_verified

  if (activeView !== 'main') {
    const wrap = (child: React.ReactNode) => <div className="p-4 sm:p-6">{child}</div>

    if (activeView === 'personal' && provider)
      return wrap(<PersonalDetailsPanel
        providerData={{
          name:            provider.name ?? '',
          phone:           provider.phone_number ?? '',
          email:           provider.email ?? '',
          specialty:       provider.specialty ?? '',
          experience:      provider.experience ?? '',
          gender:          provider.gender ?? '',
          date_of_birth:   provider.date_of_birth ?? '',
          address:         provider.address ?? '',
          qualification:   provider.qualification ?? '',
          license_number:  provider.license_number ?? '',
          languages:       provider.languages ?? [],
          specializations: provider.specializations ?? [],
          bio:             provider.bio ?? '',
        }}
        onBack={() => setActiveView('main')}
        onSaved={() => { setActiveView('main'); triggerReload() }}
      />)

    if (activeView === 'service-areas' && provider?.id)
      return wrap(<ServiceAreasPanel providerId={provider.id} onBack={() => setActiveView('main')} />)

    if (activeView === 'service-rates' && provider?.id)
      return wrap(<ServiceRatesPanel providerId={provider.id} onBack={() => setActiveView('main')} />)

    if (activeView === 'availability' && provider?.id)
      return wrap(<AvailabilityPanel providerId={provider.id} onBack={() => setActiveView('main')} />)

    if (activeView === 'payout' && provider?.id)
      return wrap(<PayoutWalletPanel providerId={provider.id} onBack={() => setActiveView('main')} />)

    if (activeView === 'security')
      return wrap(<PrivacySecurityPanel onBack={() => setActiveView('main')} providerId={provider?.id} />)

    if (activeView === 'terms')
      return wrap(<TermsPanel onBack={() => setActiveView('main')} />)

    if (activeView === 'induction')
      return wrap(<InductionPanel onBack={() => setActiveView('main')} />)

    if (activeView === 'support' && provider?.id)
      return wrap(<SupportPanel providerId={provider.id} onBack={() => setActiveView('main')} />)
  }

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-foreground">Profile</h1>
        <p className="text-sm text-muted-foreground">Manage your provider profile</p>
      </div>

      {/* Profile card */}
      <div className="bg-card border border-border p-5">
        {providerLoading ? (
          <div className="flex items-center gap-4 animate-pulse">
            <div className="w-16 h-16 rounded-full bg-muted shrink-0" />
            <div className="space-y-2 flex-1"><div className="h-5 bg-muted rounded w-1/2" /><div className="h-4 bg-muted rounded w-2/3" /><div className="h-4 bg-muted rounded w-1/3" /></div>
          </div>
        ) : (
          <div className="flex items-start gap-4">
            <div className="relative shrink-0">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                {provider?.profile_image_url ? (
                  <img src={provider.profile_image_url} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xl font-bold text-primary">{initials}</span>
                )}
                {isUploadingImage && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-full">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
              <button type="button" title="Upload profile photo" aria-label="Upload profile photo" onClick={() => fileInputRef.current?.click()} disabled={isUploadingImage} className="absolute bottom-0 right-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center border-2 border-card hover:bg-primary/90 transition-colors">
                <Camera size={11} className="text-white" />
              </button>
              <input ref={fileInputRef} type="file" title="Choose profile photo" aria-label="Choose profile photo" accept="image/jpeg,image/jpg,image/png,image/webp" className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageFile(f) }} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-foreground text-lg leading-tight">{provider?.name ?? '—'}</h2>
              <p className="text-sm text-muted-foreground mt-0.5">{provider?.specialty ?? '—'}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{provider?.email ?? '—'}</p>
              <div className="flex items-center gap-1.5 mt-1.5">
                {isVerified ? (
                  <><Shield size={13} className="text-emerald-600" /><span className="text-xs text-emerald-600 font-medium">Verified Provider</span></>
                ) : verStatus === 'pending_approval' ? (
                  <><Shield size={13} className="text-yellow-600" /><span className="text-xs text-yellow-600 font-medium">Pending Approval</span></>
                ) : (
                  <><Shield size={13} className="text-muted-foreground" /><span className="text-xs text-muted-foreground capitalize">{verStatus ?? 'Unverified'}</span></>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Provider Management */}
      <div className="bg-card border border-border overflow-hidden">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-5 pt-4 pb-2">Provider Management</p>
        <MenuRow icon={User}          title="Profile Details"  subtitle="Name, specialty, phone, gender, experience"   onClick={() => setActiveView('personal')}       disabled={providerLoading} iconBg="bg-primary/10"  iconColor="text-primary" />
        <MenuRow icon={NairaIcon}      title="Service Rates"    subtitle="Set your rates for each service type"         onClick={() => setActiveView('service-rates')} disabled={providerLoading || !provider?.id} iconBg="bg-yellow-50"  iconColor="text-yellow-600" />
        <MenuRow icon={MapPin}         title="Service Area"     subtitle="Manage cities and travel radius"              onClick={() => setActiveView('service-areas')} disabled={providerLoading || !provider?.id} iconBg="bg-blue-50"    iconColor="text-blue-500" />
        <MenuRow icon={Clock}          title="Availability"     subtitle="Set your weekly schedule"                   onClick={() => setActiveView('availability')}  disabled={providerLoading || !provider?.id} iconBg="bg-green-50"   iconColor="text-green-600" />
      </div>

      {/* Payments */}
      <div className="bg-card border border-border overflow-hidden">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-5 pt-4 pb-2">Payments</p>
        <MenuRow icon={Wallet} title="Payout & Wallet" subtitle="Bank accounts and wallet balance" onClick={() => setActiveView('payout')} disabled={providerLoading || !provider?.id} iconBg="bg-purple-50" iconColor="text-purple-600" />
      </div>

      {/* Account & Support */}
      <div className="bg-card border border-border overflow-hidden">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-5 pt-4 pb-2">Account & Support</p>
        <MenuRow icon={Shield}       title="Security"          subtitle="Password, 2FA, and security settings"              onClick={() => setActiveView('security')}  iconBg="bg-red-50"    iconColor="text-red-500" />
        <MenuRow icon={MessageCircle} title="Support"             subtitle="Phone, WhatsApp, FAQs and feedback"                onClick={() => setActiveView('support')}   iconBg="bg-emerald-50" iconColor="text-emerald-600" />
        <MenuRow icon={FileText}      title="Terms & Conditions"            subtitle="Service terms, privacy policy, code of conduct"    onClick={() => navigate('/provider/terms')} iconBg="bg-slate-100" iconColor="text-slate-500" />
        <MenuRow icon={BookOpen}      title="Provider Induction Training" subtitle="Onboarding program and platform guidelines"        onClick={() => navigate('/provider/induction?mode=revisit')} iconBg="bg-amber-50"  iconColor="text-amber-600" />
      </div>

      {/* Sign Out */}
      <button
        onClick={() => setShowSignOutModal(true)}
        disabled={isSigningOut}
        className={clsx('w-full flex items-center justify-center gap-2 py-3.5 border border border-destructive/30 text-destructive text-sm font-medium hover:bg-destructive/5 transition-colors disabled:opacity-60')}
      >
        {isSigningOut ? <div className="w-4 h-4 border-2 border-destructive border-t-transparent rounded-full animate-spin" /> : <LogOut size={16} />}
        Sign Out
      </button>

      {showSignOutModal && (
        <ConfirmationModal
          title="Sign out?"
          message="You will need to sign in again to access your provider dashboard."
          confirmLabel="Sign Out"
          confirmVariant="destructive"
          isLoading={isSigningOut}
          onConfirm={handleSignOut}
          onCancel={() => {
            if (!isSigningOut) {
              setShowSignOutModal(false)
            }
          }}
        />
      )}
    </div>
  )
}
